// SPDX-License-Identifier: GPL-3.0-or-later
//
// Pure state for the one-time "Getting started" wizard. Step completion is
// derived from live app state (folder, profile, extension, jobs, wallet,
// generated docs); skip/dismiss choices persist in localStorage. The wizard UI
// (ui/JourneyWizard.ts) renders from this — keep this module DOM-free.

export interface JourneyInputs {
  folderPicked: boolean;
  hasProfile: boolean;
  extInstalled: boolean;
  hasJobs: boolean;
  walletConnected: boolean;
  hasGeneratedDoc: boolean;
}

export interface JourneyStep {
  id: string;
  title: string;
  done: boolean;
  skippable: boolean;
  skipped: boolean;
}

const DISMISS_KEY = 'applify:onboarding:dismissed';
const SKIP_PREFIX = 'applify:onboarding:skipped:';

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string, on: boolean): void {
  try {
    if (on) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

export function isJourneyDismissed(): boolean {
  return readFlag(DISMISS_KEY);
}
export function dismissJourney(): void {
  writeFlag(DISMISS_KEY, true);
}
export function isStepSkipped(id: string): boolean {
  return readFlag(SKIP_PREFIX + id);
}
export function skipStep(id: string): void {
  writeFlag(SKIP_PREFIX + id, true);
}

const STEP_DEFS: ReadonlyArray<{
  id: string;
  title: string;
  skippable: boolean;
  done: (i: JourneyInputs) => boolean;
}> = [
  { id: 'folder', title: 'Pick a folder', skippable: false, done: (i) => i.folderPicked },
  { id: 'profile', title: 'Add your profile', skippable: true, done: (i) => i.hasProfile },
  { id: 'extension', title: 'Install the extension', skippable: true, done: (i) => i.extInstalled },
  { id: 'firstJob', title: 'Save your first job', skippable: true, done: (i) => i.hasJobs },
  { id: 'wallet', title: 'Connect your AI wallet', skippable: true, done: (i) => i.walletConnected },
  { id: 'firstDoc', title: 'Generate a document', skippable: true, done: (i) => i.hasGeneratedDoc },
];

export function buildSteps(inputs: JourneyInputs): JourneyStep[] {
  return STEP_DEFS.map((d) => ({
    id: d.id,
    title: d.title,
    done: d.done(inputs),
    skippable: d.skippable,
    skipped: isStepSkipped(d.id),
  }));
}

// First step that's neither done nor skipped, or -1 if everything is resolved.
export function currentStepIndex(steps: JourneyStep[]): number {
  return steps.findIndex((s) => !s.done && !s.skipped);
}

export function isJourneyComplete(steps: JourneyStep[]): boolean {
  return steps.every((s) => s.done || s.skipped);
}

export function isJourneyActive(inputs: JourneyInputs): boolean {
  if (isJourneyDismissed()) return false;
  return !isJourneyComplete(buildSteps(inputs));
}

// In-memory Back/forward navigation override (not persisted; survives the app's
// full re-renders because the module instance persists across them).
let manualIndex: number | null = null;
export function getManualIndex(): number | null {
  return manualIndex;
}
export function setManualIndex(i: number | null): void {
  manualIndex = i;
}
